import { User } from "firebase/auth";
import { apiFetch, ApiError } from "./client";

export type LeagueMode = "TARGET" | "FINAL_POSITIONS" | "GAMEWEEK_CHALLENGE" | "SCORE_PREDICTOR" | "SYNDICATE";
export type MemberStatus = "PENDING" | "APPROVED" | "VIEWER";
export type LeagueStatus = "PREPARING" | "RUNNING" | "COMPLETED";
export type TargetState = "BUST" | "ELIGIBLE" | "INELIGIBLE";

export type SummaryEventType =
  | "LEAD_CHANGE"
  | "TIED_POSITIONS"
  | "TARGET_BUST"
  | "TARGET_HIT"
  | "GW_TOP_SCORER"
  | "ALL_TIED_GW_SCORE"
  | "HIGH_SCORE"
  | "BIG_RISER"
  | "BIG_FALLER"
  | "CLOSE_GAP"
  | "BIG_LEAD"
  | "BOTTOM_GAP"
  | "STILL_LEADING"
  | "BLANK_GAMEWEEK"
  | "GWC_SCORE_DIST"
  | "SP_EXACT_DIST"
  | "SEASON_WINNER"
  | "SEASON_RUNNER_UP"
  | "SEASON_THIRD"
  | "SEASON_LAST"
  | "SEASON_TARGET_BUSTS";

export interface SummaryEvent {
  type: SummaryEventType;
  memberIds: string[];
  params: Record<string, string | number | boolean | null>;
}

export interface GwSummary {
  gameweekNumber: number;
  gameweekName: string;
  gameweekShortName: string | null;
  isSeasonSummary?: boolean;
  events: SummaryEvent[];
}

export interface LeagueSummary {
  id: string;
  name: string;
  inviteCode: string;
  mode: LeagueMode;
  status: LeagueStatus;
  archived: boolean;
  membershipStatus: MemberStatus;
  isLeagueAdmin: boolean;
  score: number;
  delta: number;
  targetState: TargetState | null;
  totalGoals: number;
  totalAssists: number;
  myMemberId: string | null;
  rules: {
    mode: LeagueMode;
    target: number | null;
  };
  competition: {
    id: string;
    name: string;
    season: string;
    type: string;
  };
  memberCount: number;
  myRank: number | null;
  /** Drives the "unseen gameweek recap" indicator — compared client-side against
   * localStorage's last-seen value (see lib/gwRecapSeen.ts), since "seen" is deliberately
   * not stored server-side. Null while the league hasn't started / no GW has completed. */
  latestCompletedGwNumber: number | null;
  startGameweek: { number: number; name: string } | null;
  isLocked?: boolean | null;
}

export interface LeagueMemberSummary {
  id: string;
  userId: string | null;
  displayName: string;
  guestDisplayName: string | null;
  status: MemberStatus;
  score: number;
  delta: number;
  targetState: TargetState | null;
  scoredCount?: number;
  isReady: boolean;
  isTeamValid: boolean;
  syndicateTiebreakerGoals?: number | null;
  syndicatePickCount?: number;
  user: { id: string; displayName: string; firebaseId: string } | null;
  selections: { id: string; player: { id: string; name: string; club: string } }[];
  firebaseId?: string;
}

export interface LeagueDetail {
  id: string;
  name: string;
  inviteCode: string;
  adminId: string;
  mode: LeagueMode;
  target: number;
  competitionId: string;
  status: LeagueStatus;
  competition: {
    id: string;
    name: string;
    season: string;
    status: "UPCOMING" | "ACTIVE" | "COMPLETED";
    type: "CLUB" | "INTERNATIONAL";
    gameweeks: { id: string; number: number; name: string; shortName: string | null; completedAt: string | null }[];
  };
  startGameweek: { id: string; number: number; name: string } | null;
  members: LeagueMemberSummary[];
  activeGameweek: { id: string; number: number; name: string } | null;
  myMemberId: string | null;
  isLeagueAdmin: boolean;
  // Final Positions fields
  isLocked?: boolean;
  fpClubs?: { id: string; name: string; shortName: string | null; crest: string | null }[];
  latestStandings?: { clubId: string; position: number }[];
  myPrediction?: { id: string; clubId: string; position: number }[];
  fpScoringConfig?: { topTierSize: number | null; relegationSize: number | null; championEnabled: boolean; topTierLabel: string | null; relegationLabel: string | null };
  gwHighlights: {
    gameweek: string;
    memberId: string | null;
    displayName: string | null;
    score: number;
  }[];
  gwSummary: GwSummary | null;
  leagueGwAverage?: number;
  rankHistory?: { gwNumber: number; gwName: string; gwShortName: string; rank: number; score: number }[];
  // Syndicate fields
  syndicatePotCount?: number;
  syndicateGoalsToDate?: number;
}

export interface MemberDetail {
  member: {
    id: string;
    userId: string | null;
    guestDisplayName: string | null;
    status: MemberStatus;
    user: { id: string; displayName: string; firebaseId: string } | null;
    selections: { id: string; player: { id: string; name: string; club: string } }[];
  };
  score: number;
  delta: number;
  targetState: TargetState | null;
  rank: number | null;
  chartData: { gwName: string; cumulative: number; gwPoints: number }[];
  playerStats: { playerId: string; name: string; club: string; goals: number; assists: number }[];
}

export interface CreateLeaguePayload {
  name: string;
  competitionId: string;
  startGameweekId?: string;
  mode: LeagueMode;
  target?: number;
}

export function fetchMyLeagues(user: User): Promise<LeagueSummary[]> {
  return apiFetch(user, "/leagues");
}

export function fetchLeague(user: User, id: string): Promise<LeagueDetail> {
  return apiFetch(user, `/leagues/${id}`);
}

// ─── Gameweek recap ─────────────────────────────────────────────────────────────

export interface TargetGwBreakdown {
  type: "TARGET";
  players: { playerId: string; name: string; goals: number; points: number }[];
  targetState: TargetState | null;
  becameEligibleThisGw: boolean;
  totalGoals: number;
  target: number | null;
}

export interface FPGwBreakdown {
  type: "FINAL_POSITIONS";
  current: { base: number; bullseye: number; champion: number; topTier: number; relegation: number; total: number };
  previous: { base: number; bullseye: number; champion: number; topTier: number; relegation: number; total: number } | null;
}

export interface GwcGwBreakdown {
  type: "GAMEWEEK_CHALLENGE";
  playerName: string | null;
  playerClub: string | null;
  goals: number;
  points: number;
  /** Season-summary only: total goals across every pick this season. */
  seasonTotalGoals?: number;
  /** Season-summary only: the single highest-scoring pick across the season, null if no pick
   * ever scored (or there were no completed gameweeks to pick in). */
  bestPick?: { gwName: string; playerName: string; playerClub: string | null; goals: number } | null;
}

export interface SPGwBreakdown {
  type: "SCORE_PREDICTOR";
  /** Populated for a single-gameweek recap only — empty for a season summary (see seasonStats). */
  fixtures: {
    fixtureId: string;
    homeClub: string;
    awayClub: string;
    homeScore: number | null;
    awayScore: number | null;
    myHomeScore: number | null;
    myAwayScore: number | null;
    points: number;
  }[];
  /** Season-summary only: an aggregate in place of a 100+-fixture list. */
  seasonStats?: { exactScores: number; correctResults: number; totalPredictions: number; points: number };
}

export interface SyndicateGwBreakdown {
  type: "SYNDICATE";
  pots: { potId: string; label: string; pickName: string | null; points: number; potDelta: number }[];
}

export type GwRecapBreakdown = TargetGwBreakdown | FPGwBreakdown | GwcGwBreakdown | SPGwBreakdown | SyndicateGwBreakdown;

export type GwRecap =
  | { available: false; reason: "NOT_STARTED" | "NOT_READY" }
  | {
      available: true;
      gameweekId: string;
      gameweekNumber: number;
      gameweekName: string;
      isSeasonSummary: boolean;
      mode: LeagueMode;
      myScore: number;
      myDelta: number;
      /** Only populated (non-null) for a season summary — the running recap has no "final" rank yet. */
      finalRank: number | null;
      totalReadyMembers: number | null;
      breakdown: GwRecapBreakdown;
    };

export function fetchGwRecap(user: User, leagueId: string): Promise<GwRecap> {
  return apiFetch(user, `/leagues/${leagueId}/gw-recap`);
}

export function fetchMember(user: User, leagueId: string, memberId: string): Promise<MemberDetail> {
  return apiFetch(user, `/leagues/${leagueId}/members/${memberId}`);
}

export function createLeague(user: User, payload: CreateLeaguePayload): Promise<LeagueDetail> {
  return apiFetch(user, "/leagues", { method: "POST", body: JSON.stringify(payload) });
}

export function joinLeague(user: User, inviteCode: string): Promise<{ id: string; name: string }> {
  return apiFetch(user, "/leagues/join", { method: "POST", body: JSON.stringify({ inviteCode }) });
}

export interface LeagueLookupResult {
  id: string;
  name: string;
  mode: LeagueMode;
  competition: { name: string; season: string };
  memberCount: number;
  // A league can no longer be joined once it has started — there is no "join as viewer"
  // fallback, only whether joining is possible at all.
  joinable: boolean;
  alreadyMember: boolean;
}

export function lookupLeagueByCode(user: User, code: string): Promise<LeagueLookupResult> {
  return apiFetch(user, `/leagues/lookup/${encodeURIComponent(code)}`);
}

/**
 * Same endpoint as lookupLeagueByCode, but works for a logged-out visitor — the public
 * /join/:code landing page's data source. Attaches a token when a user is available (so
 * alreadyMember comes back correctly for someone already signed in); omits it otherwise
 * rather than requiring sign-in just to preview what a shared invite link points to.
 */
export async function lookupLeagueByCodePublic(code: string, user?: User | null): Promise<LeagueLookupResult> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (user) headers.Authorization = `Bearer ${await user.getIdToken()}`;
  const res = await fetch(`${process.env.REACT_APP_API_URL ?? ""}/leagues/lookup/${encodeURIComponent(code)}`, { headers });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body.error ?? res.statusText);
  }
  return res.json();
}

export function deleteLeague(user: User, leagueId: string): Promise<void> {
  return apiFetch(user, `/leagues/${leagueId}`, { method: "DELETE" });
}

export function saveSelections(user: User, leagueId: string, playerIds: string[]): Promise<void> {
  return apiFetch(user, `/leagues/${leagueId}/selections`, {
    method: "POST",
    body: JSON.stringify({ playerIds }),
  });
}

export function addGuest(user: User, leagueId: string, guestDisplayName: string): Promise<{ id: string }> {
  return apiFetch(user, `/leagues/${leagueId}/guests`, {
    method: "POST",
    body: JSON.stringify({ guestDisplayName }),
  });
}

export function removeMember(user: User, leagueId: string, memberId: string): Promise<void> {
  return apiFetch(user, `/leagues/${leagueId}/members/${memberId}`, { method: "DELETE" });
}


export function setGuestSelections(user: User, leagueId: string, memberId: string, playerIds: string[]): Promise<void> {
  return apiFetch(user, `/leagues/${leagueId}/members/${memberId}/selections`, {
    method: "POST",
    body: JSON.stringify({ playerIds }),
  });
}

export interface LeagueExportMember {
  id: string;
  displayName: string;
  players: { name: string; club: string; goals: number; assists: number }[];
  lastGwcPick: { gameweekName: string; playerName: string; goals: number | null } | null;
  exactHits: number | null;
}

export interface LeagueExportData {
  leagueName: string;
  competitionName: string;
  season: string;
  mode: string;
  leagueStatus: "PREPARING" | "RUNNING" | "COMPLETED";
  rules: { target: number | null };
  members: LeagueExportMember[];
}

export function fetchLeagueExport(user: User, leagueId: string): Promise<LeagueExportData> {
  return apiFetch(user, `/leagues/${leagueId}/export`);
}

export interface LeagueFixture {
  id: string;
  homeClub: { id: string; name: string; shortName: string | null; crest: string | null };
  awayClub: { id: string; name: string; shortName: string | null; crest: string | null };
  homeScore: number | null;
  awayScore: number | null;
  kickoffTime: string | null;
  status: string;
  decidedBy: string | null;
  penaltyHomeScore: number | null;
  penaltyAwayScore: number | null;
  myPrediction?: { homeScore: number; awayScore: number } | null;
}

export interface LeagueGwFixtures {
  gameweek: {
    id: string;
    number: number;
    name: string;
    fixtures: LeagueFixture[];
  };
}

export interface LeagueGwScorers {
  gameweek: {
    id: string;
    number: number;
    name: string;
    scorers: { playerId: string; playerName: string; playerClub: string; goals: number }[];
  };
}

export function fetchLeagueFixtures(user: User, leagueId: string, gameweekId: string): Promise<LeagueGwFixtures> {
  return apiFetch(user, `/leagues/${leagueId}/fixtures?gameweekId=${encodeURIComponent(gameweekId)}`);
}

export function fetchLeagueGwScorers(user: User, leagueId: string, gameweekId: string): Promise<LeagueGwScorers> {
  return apiFetch(user, `/leagues/${leagueId}/gw-scorers?gameweekId=${encodeURIComponent(gameweekId)}`);
}

export interface LeagueGwStats {
  gameweek: {
    id: string;
    number: number;
    name: string;
    scorers: { playerId: string; playerName: string; playerClub: string; goals: number }[];
    assisters: { playerId: string; playerName: string; playerClub: string; assists: number }[];
    carded: { playerId: string; playerName: string; playerClub: string; yellowCards: number; redCards: number }[];
  };
}

export function fetchLeagueGwStats(user: User, leagueId: string, gameweekId: string): Promise<LeagueGwStats> {
  return apiFetch(user, `/leagues/${leagueId}/gw-stats?gameweekId=${encodeURIComponent(gameweekId)}`);
}

